<template>
  <main class="container">
    <div class="row">
      <article class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 d-grid mx-auto mt-4 mt-md-5">
        <NuxtImg
            :width="$device.isMobile ? 380 : 750"
            :height="$device.isMobile ? 200 : 400"
            placeholder
            :src="data.image_url"
            preload
            class="w-100 rounded mb-5 object-fit-cover"
            :alt="data.title"
        />
        <div class="d-flex text-center justify-content-center">
          <span v-for="tag in data.tags" class="badge bg-primary rounded-pill mb-4 mx-1">#{{ tag }}</span>
        </div>
        <h1 class="mb-4 mb-md-5 text-center">{{ data.title }}</h1>
        <p class="fw-bold" v-if="data.description" v-html="data.description"></p>
        <template v-for="(part, index) in formattedContent">
          <ImageGrid v-if="part.type === 'ImageGrid'" :images="part.content" :key="'ImageGrid-' + index" />
          <PromoRewardsTable v-else-if="part.type === 'PromoRewardsTable'" :month="part.month" :year="part.year" :type="part.rewardType" :key="'PromoRewardsTable-' + index" />
          <div v-else class="table-responsive-md" v-html="part.content" :key="'content-' + index" />
        </template>
        <hr class="border-top mt-3">
        <p class="small mb-0 opacity-75">
          Bijgewerkt op {{ moment(data.updated_at).format('DD MMM YYYY') }} -
          <NuxtLink
              v-if="data.alternative"
              :to="{ name: 'blog-slug', params: { slug: data.alternative.slug }}"
              class="text-white text-decoration-underline"
              :title="data.alternative.title">Read in English</NuxtLink>
        </p>
      </article>
    </div>
  </main>
</template>

<script setup lang="ts">
import moment from "moment";
import { formatContent } from '~/helpers/formatContent.js';
import ImageGrid from '~/components/content/ImageGrid.vue';
import PromoRewardsTable from "~/components/content/PromoRewardsTable.vue";

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const {data: fetchedData} = await useAsyncData(
    'blog-' + route.params.slug,
    async () =>
        $fetch(runtimeConfig.public.backendUrl + "/api/v2/blogs/" + route.params.slug)
);


if (!fetchedData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Geen blogs gevonden'
  })
}

const data = ref(fetchedData.value.blog);
const formattedContent = computed(() => {
  return formatContent(data.value.content);
});

useSeoMeta({
  title: data.value.meta_title,
  ogTitle: data.value.meta_title,
  twitterTitle: data.value.meta_title,
  description: data.value.meta_description,
  ogDescription: data.value.meta_description,
  twitterDescription: data.value.meta_description,
  ogUrl: runtimeConfig.public.baseUrl + "/nl/blog/" + data.value.slug,
  ogImage: data.value.image_url,
  twitterImage: data.value.image_url,
  twitterImageAlt: 'Flying Blue Promo Rewards',
  ogType: "article",
  articlePublishedTime: moment(data.value.created_at).toISOString(),
  articleModifiedTime: moment(data.value.updated_at).toISOString(),
  articleTag: data.value.tags,
  ogLocale: "nl",
  ogLocaleAlternate: "en",
});

useHead({
  htmlAttrs: {
    lang: 'nl',
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/nl/blog/" + data.value.slug
    },
    ...(data.value.alternative ? [
      {
        rel: "alternate",
        hreflang: "x-default",
        href: runtimeConfig.public.baseUrl + "/blog/" + data.value.alternative.slug
      },
      {
        rel: "alternate",
        hreflang: "en",
        href: runtimeConfig.public.baseUrl + "/blog/" + data.value.alternative.slug
      }
    ] : []),
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/blog/" + data.value.slug
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl + '/nl',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: runtimeConfig.public.baseUrl + '/nl/blog',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.title
          },
        ],
      },
      {
        '@type': 'Article',
        headline: data.value.title,
        datePublished: data.value.created_at,
        dateModified: data.value.updated_at,
        inLanguage: "nl",
        description: data.value.description,
        keywords: data.value.tags,
        image: data.value.image_url,
        articleBody: data.value.content,
        publisher: {
          '@type': 'Organization',
          name: 'Reminder for Flying Blue',
          logo: runtimeConfig.public.baseUrl + '/icon.png',
          slogan: 'Never miss out on earning Flying Blue Miles!',
          url: runtimeConfig.public.baseUrl,
          sameAs: [
            'https://www.instagram.com/rewards.flights/',
            'https://x.com/rewardsflights',
            'https://pinterest.com/rewardsflights',
            'https://www.facebook.com/rewardsflights',
            'https://www.linkedin.com/company/rewards-flights',
            'https://www.threads.net/@rewards.flights',
            'https://bsky.app/profile/rewards.flights'
          ]
        },
        author: [
          {
            '@type': 'Person',
            name: 'Bjorn Antonissen',
            alternateName: 'Bjornftw',
            url: 'https://www.bjornantonissen.nl',
            sameAs: [
              'https://www.instagram.com/bjornftw/',
              'https://x.com/bjornftw',
            ],
          },
        ],
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': runtimeConfig.public.baseUrl + '/nl/blog/' + data.value.slug
        },
      }
    ]
  }
});
</script>